import * as React from 'react'
import { Plus, X } from 'lucide-react'

import {
  AUTH_FIELDS,
  AUTH_FIELD_LABELS,
  type AuthField,
} from '@/features/tools/components/authenticate-caller/authConstants'
import type { MethodRow } from './authenticationDraft'

function FieldChip({
  field,
  onRemove,
  disabled,
}: {
  field: string
  onRemove?: () => void
  disabled?: boolean
}) {
  const label = AUTH_FIELD_LABELS[field as AuthField] ?? field
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-sm text-slate-800">
      {label}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
          aria-label={`Remove ${label}`}>
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  )
}

function AddFieldButton({
  usedFields,
  onAdd,
  disabled,
}: {
  usedFields: Set<string>
  onAdd: (field: string) => void
  disabled?: boolean
}) {
  const [open, setOpen] = React.useState(false)
  const available = AUTH_FIELDS.filter((f) => !usedFields.has(f))

  if (available.length === 0) return null

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50">
        <Plus className="h-3 w-3" /> Field
      </button>
      {open && (
        <div className="absolute top-full left-0 z-10 mt-1 max-h-48 w-44 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {available.map((f) => (
            <button
              key={f}
              type="button"
              className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-indigo-50"
              onClick={() => {
                onAdd(f)
                setOpen(false)
              }}>
              {AUTH_FIELD_LABELS[f]}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/** "all of:" / "N of:" selector. Hides itself when there's only 1 field. */
function MatchCountSelector({
  row,
  onChange,
  disabled,
}: {
  row: MethodRow
  onChange: (n: number | 'all') => void
  disabled?: boolean
}) {
  const fieldCount = row.fields.length
  if (fieldCount <= 1) return null

  const value = row.n === 'all' ? 'all' : String(row.n)

  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => {
        const v = e.target.value
        onChange(v === 'all' ? 'all' : parseInt(v, 10))
      }}
      className="rounded-md border border-slate-200 bg-white px-1.5 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50">
      <option value="all">all of:</option>
      {Array.from({ length: fieldCount - 1 }, (_, i) => i + 1).map((n) => (
        <option key={n} value={n}>
          {n} of:
        </option>
      ))}
    </select>
  )
}

export function MethodRowEditor({
  index,
  row,
  onUpdateRow,
  onRemoveRow,
  disabled,
}: {
  index: number
  row: MethodRow
  onUpdateRow: (row: MethodRow) => void
  onRemoveRow: () => void
  disabled?: boolean
}) {
  const usedFields = new Set(row.fields)

  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
      <span className="w-5 shrink-0 text-center text-sm text-slate-400">
        {index + 1}
      </span>
      <MatchCountSelector
        row={row}
        disabled={disabled}
        onChange={(n) => {
          // Clamp n to be at most fieldCount - 1; "all" stays 'all'
          if (n === 'all') {
            onUpdateRow({ ...row, n: 'all' })
          } else {
            const max = Math.max(1, row.fields.length - 1)
            onUpdateRow({ ...row, n: Math.min(n, max) })
          }
        }}
      />
      <div className="flex flex-1 flex-wrap items-center gap-2">
        {row.fields.map((field) => (
          <FieldChip
            key={field}
            field={field}
            disabled={disabled}
            onRemove={
              !disabled
                ? () => {
                    const next = row.fields.filter((f) => f !== field)
                    if (next.length === 0) {
                      onRemoveRow()
                    } else {
                      // Clamp n if it's now too large
                      let nextN = row.n
                      if (typeof nextN === 'number' && nextN >= next.length) {
                        nextN = next.length === 1 ? 'all' : next.length - 1
                      }
                      onUpdateRow({ fields: next, n: nextN })
                    }
                  }
                : undefined
            }
          />
        ))}
        <AddFieldButton
          usedFields={usedFields}
          disabled={disabled}
          onAdd={(f) => onUpdateRow({ ...row, fields: [...row.fields, f] })}
        />
      </div>
    </div>
  )
}
